/**
 * Password Utility Functions
 * Handles password hashing and validation
 */
import bcrypt from 'bcryptjs';
import logger from './logger';

/**
 * Hash a password
 * @param {string} password - Plain text password
 * @returns {Promise<string>} - Hashed password
 */
export const hashPassword = async (password) => {
  try {
    if (!password || password.trim().length === 0) {
      throw new Error('Password cannot be empty');
    }
    
    const saltRounds = 10;
    const hashedPassword = await bcrypt.hash(password, saltRounds);
    
    logger.debug('Password hashed successfully', { operation: 'hashPassword' });
    return hashedPassword;
  } catch (error) {
    logger.error('Error hashing password', error, { operation: 'hashPassword' });
    throw error;
  }
};

/**
 * Compare password with hash
 * @param {string} password - Plain text password
 * @param {string} hash - Hashed password
 * @returns {Promise<boolean>} - True if password matches
 */
export const comparePassword = async (password, hash) => {
  try {
    if (!password || !hash) {
      return false;
    }
    
    const isMatch = await bcrypt.compare(password, hash);
    logger.debug('Password comparison completed', { 
      operation: 'comparePassword',
      isMatch 
    });
    return isMatch;
  } catch (error) {
    logger.error('Error comparing password', error, { operation: 'comparePassword' });
    return false;
  }
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {Object} - { valid: boolean, message: string }
 */
export const validatePasswordStrength = (password) => {
  if (!password) {
    return { valid: false, message: 'Password is required' };
  }
  
  if (password.length < 6) {
    return { valid: false, message: 'Password must be at least 6 characters long' };
  }
  
  if (password.length > 128) {
    return { valid: false, message: 'Password must be less than 128 characters' };
  }
  
  return { valid: true, message: 'Password is valid' };
};

